import { Modal, notification, Upload, UploadProps } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import { useEffect, useState } from 'react';
import APIClient from '../api/api';
import { Job, JobType } from '../api/models';
import { formatDateTime } from '../utils';
import { API_BASE_URL } from '../config';
import { Button, Column, DataTable, Pagination, StatusTag, TagTone } from '../components/ui';
import { PageFooter, PageHead, PageToolbar } from '../layout';
import { ImportModal, ImportModalConfig } from '../components/ImportModal';
import { pricingUploadErrorMessage } from './pricingUpload';

const switchListConfig: ImportModalConfig = {
  title: 'Import Afternic Switch List',
  description: 'Upload the Afternic switch list file.',
  action: '/v1/afternic/uploadSwitchList',
  successMessage: 'List successfully uploaded.',
  errorMessage: 'List failed to upload.',
};

const DEFAULT_PAGE_SIZE = 25;

const statusTone: Record<string, TagTone> = {
  running: 'accent',
  success: 'success',
  failed: 'danger',
};

const Inventory = () => {
  const [jobs, setJobs] = useState<Job[]>();
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [loading, setLoading] = useState(false);
  const [showFailedDomains, setShowFailedDomains] = useState<string[]>();
  const [showSwitchListImport, setShowSwitchListImport] = useState(false);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    loadJobs(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageSize]);

  async function loadJobs(p: number) {
    setLoading(true);
    try {
      const response = await APIClient.listJobs({ page: p, pageSize: pageSize }, { type: [JobType.UpdateListings] });
      setJobs(response.content);
      setTotal(response.total);
      setPage(response.currentPage || p);
    } catch (e) {
      notification.error({ message: 'Failed to load jobs at this time.' });
    } finally {
      setLoading(false);
    }
  }

  const uploadProps: UploadProps = {
    name: 'uploadPricing',
    action: API_BASE_URL + '/v1/sales/uploadPricing',
    method: 'POST',
    showUploadList: false,
    headers: APIClient.getAuthHeaders(),
    onChange: (data) => {
      if (data?.file?.status === 'done') {
        const processed: number = data?.file?.response?.processed || 0;
        notification.success({
          description: processed.toLocaleString() + ' records queued for processing.',
          message: 'Upload Success',
        });
        setTimeout(() => loadJobs(page), 1000);
      } else if (data?.file?.status === 'error') {
        notification.error({
          message: 'Upload Failed',
          description: pricingUploadErrorMessage(data.file),
          duration: 0,
        });
      }
    },
  };

  function exportFailedDomainsCSV() {
    if (!showFailedDomains || showFailedDomains.length === 0) return;
    const csvContent = ['domains', ...showFailedDomains].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'failed_domains.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  const columns: Column<Job>[] = [
    {
      key: 'sourceFile',
      title: 'Filename',
      render: (job) => {
        try {
          return <span className="text-text font-medium">{JSON.parse(atob(job.jobData || '')).sourceFile}</span>;
        } catch {
          return <span className="text-text-3">Unknown</span>;
        }
      },
    },
    {
      key: 'startedAt',
      title: 'Started',
      render: (job) => (
        <span className="font-mono text-[12.5px] text-text-2 tabular-nums">
          {formatDateTime(job.startedAt)}
        </span>
      ),
    },
    {
      key: 'finishedAt',
      title: 'Finished',
      render: (job) => (
        <span className="font-mono text-[12.5px] text-text-2 tabular-nums">
          {job.finishedAt ? formatDateTime(job.finishedAt) : <span className="text-accent">running…</span>}
        </span>
      ),
    },
    {
      key: 'status',
      title: 'Status',
      render: (job) => {
        const tone = statusTone[job.status] ?? 'neutral';
        return <StatusTag tone={tone}>{job.status}</StatusTag>;
      },
    },
    {
      key: 'failures',
      title: 'Failures',
      align: 'right',
      render: (job) => {
        try {
          const failures = JSON.parse(atob(job.jobData || '')).failedDomains || [];
          if (failures.length === 0) {
            return <span className="font-mono text-[12.5px] text-text-3">0</span>;
          }
          return (
            <button
              type="button"
              onClick={() => setShowFailedDomains(failures)}
              className="font-mono text-[12.5px] text-danger font-semibold hover:underline"
            >
              {failures.length}
            </button>
          );
        } catch {
          return <span className="text-text-3">—</span>;
        }
      },
    },
  ];

  const downloadParams = { portfolio: true, needsPricing: true };

  return (
    <>
      <PageHead
        title="Inventory"
        subtitle="Pricing update jobs and outstanding domains needing prices."
        actions={
          <>
            <Button variant="ghost" loading={downloading} onClick={async () => {
              setDownloading(true);
              try {
                const params = APIClient.getListDomainLeadsParams(downloadParams);
                const resp = await APIClient.instance.post('/v1/domainLeads', null, {
                  params: { ...params, download: true },
                  responseType: 'arraybuffer',
                });
                const ct = resp.headers['content-type'] || '';
                const isCSV = ct.includes('text/csv');
                const blob = new Blob([resp.data], { type: isCSV ? 'text/csv' : 'application/zip' });
                const a = document.createElement('a');
                a.href = URL.createObjectURL(blob);
                a.download = isCSV ? 'needs-pricing.csv' : 'needs-pricing.zip';
                a.click();
                URL.revokeObjectURL(a.href);
              } catch (e) {
                notification.error({ message: 'Download failed.' });
              } finally {
                setDownloading(false);
              }
            }}>Download Needs-Pricing</Button>
            <Button variant="ghost" onClick={() => setShowSwitchListImport(true)}>
              Switch List
            </Button>
            <Upload {...uploadProps}>
              <Button variant="primary" iconLeft={<UploadOutlined />}>Upload Pricing</Button>
            </Upload>
          </>
        }
      />

      <PageToolbar>
        <span className="text-[11px] text-text-3 font-mono">
          {jobs ? <>total <b className="text-text-2">{total.toLocaleString()}</b></> : 'loading…'}
        </span>
      </PageToolbar>

      <DataTable
        columns={columns}
        data={jobs ?? []}
        rowKey={(j) => String(j.id)}
        loading={loading}
        emptyMessage="No pricing jobs yet."
      />
      <PageFooter
        left={
          <span>
            Showing <b className="text-text-2">{jobs?.length ?? 0}</b> of <b className="text-text-2">{total.toLocaleString()}</b>
          </span>
        }
        right={<Pagination page={page} pageSize={pageSize} total={total} onPageChange={loadJobs} onPageSizeChange={(s) => { setPageSize(s); setPage(1); }} loading={loading} />}
      />

      {showSwitchListImport && (
        <ImportModal config={switchListConfig} onClose={() => setShowSwitchListImport(false)} />
      )}

      {showFailedDomains && (
        <Modal
          open
          title="Failed Domains"
          footer={null}
          onCancel={() => setShowFailedDomains(undefined)}
        >
          <div className="flex justify-between items-center mb-3">
            <span className="text-text-2 text-[12px]">{showFailedDomains.length} domains</span>
            <button
              type="button"
              onClick={exportFailedDomainsCSV}
              className="text-accent text-[12px] font-semibold hover:opacity-80"
            >
              Export CSV
            </button>
          </div>
          <div className="max-h-[400px] overflow-auto border border-border rounded-md p-2 font-mono text-[12px] text-text-2 bg-bg">
            {showFailedDomains.map((d, i) => (
              <div key={i}>{d}</div>
            ))}
          </div>
        </Modal>
      )}
    </>
  );
};

export default Inventory;
